/**
 * Newsletter Helper Functions
 *
 * Utilities for preparing newsletter subscription data from checkout form.
 */

import type { ContactPerson } from '@/types/schemas/checkout/contact';
import type { Passenger } from '@/types/checkout';
import type { NewsletterSubscriptionPayload } from '@/types/schemas/crm/newsletter';

/**
 * Convert German date format (DD.MM.YYYY) to ISO format (YYYY-MM-DD)
 *
 * @param germanDate - Date in format "DD.MM.YYYY"
 * @returns Date in format "YYYY-MM-DD"
 *
 * @example
 * convertGermanDateToISO('23.01.1974') // Returns '1974-01-23'
 */
export function convertGermanDateToISO(germanDate: string): string {
  const [day, month, year] = germanDate.split('.');
  return `${year}-${month}-${day}`;
}

/**
 * Map salutation to gender for CRM API
 *
 * @param salutation - Salutation value ('mr', 'mrs', or 'diverse')
 * @returns Gender value for CRM API ('male', 'female', or '')
 *
 * @example
 * mapSalutationToGender('mr')      // Returns 'male'
 * mapSalutationToGender('mrs')     // Returns 'female'
 * mapSalutationToGender('diverse') // Returns ''
 */
export function mapSalutationToGender(
  salutation: 'mr' | 'mrs' | 'diverse'
): '' | 'male' | 'female' {
  switch (salutation) {
    case 'mr':
      return 'male';
    case 'mrs':
      return 'female';
    case 'diverse':
    default:
      return '';
  }
}

/**
 * Build newsletter subscription payload from checkout data.
 *
 * The newsletter subscriber is always the contact person, so the payload is built
 * in both travel modes. The salutation/birthDate-derived fields (gender, birthdate)
 * are only included when the contact person is traveling ("I travel myself"), which
 * is exactly when those inputs are visible in the UI. In accompany mode those fields
 * are hidden and not collected, so they are omitted (gender becomes an empty string,
 * birthdate is left off entirely — it is optional in NewsletterSubscriptionSchema).
 *
 * @param contactPerson - Contact person data from checkout form
 * @param passengers - Passengers array from checkout store
 * @param productCode - Product code from checkout store
 * @param isTraveling - Whether the contact person travels (invoiceRecipient === 'solo')
 * @returns Newsletter subscription payload ready for CRM API, or null if email is missing
 */
export function buildNewsletterPayload(
  contactPerson: ContactPerson,
  passengers: Passenger[],
  productCode: string,
  isTraveling: boolean
): NewsletterSubscriptionPayload | null {
  if (!contactPerson.email) {
    return null;
  }

  const adultCount = passengers.filter((p) => p.isAdult).length;
  const childCount = passengers.filter((p) => p.isChild).length;

  return {
    email: contactPerson.email,
    firstname: contactPerson.firstName,
    lastname: contactPerson.lastName,
    gender:
      isTraveling && contactPerson.salutation
        ? mapSalutationToGender(contactPerson.salutation)
        : '',
    zip: contactPerson.postalCode,
    ...(isTraveling && contactPerson.birthDate
      ? { birthdate: convertGermanDateToISO(contactPerson.birthDate) }
      : {}),
    source: 'checkout',
    adults: adultCount.toString(),
    children: childCount.toString(),
    productId: productCode,
  };
}
